import { Link } from 'react-router-dom';
import type { NewsItem } from '@/types';
import SectionHeading from '@/components/base/SectionHeading';

const CATEGORY_LABEL: Record<string, string> = {
  movie: '电影新闻',
  tv: '电视剧新闻',
  review: '影评专题',
};

interface NewsSectionProps {
  items: NewsItem[];
}

export default function NewsSection({ items }: NewsSectionProps) {
  const [lead, ...rest] = items;

  return (
    <section className="w-full px-4 md:px-8 py-12 md:py-16">
      <SectionHeading
        title="最新影视资讯"
        subtitle="电影、电视剧与行业动态，第一时间掌握"
        moreLabel="全部资讯"
        moreTo="/news"
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {lead && (
          <Link
            to={`/news/${lead.id}`}
            className="group relative rounded-lg overflow-hidden border border-background-200/70 min-h-[280px] md:min-h-[340px] flex"
          >
            <img
              src={lead.cover}
              alt={lead.title}
              title={`${lead.title} 影视资讯`}
              className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent"></div>
            <div className="relative z-10 mt-auto p-5 md:p-6">
              <span className="inline-block px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-semibold mb-3">
                {CATEGORY_LABEL[lead.category]}
              </span>
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 line-clamp-2 group-hover:text-accent-400 transition-colors">
                {lead.title}
              </h3>
              <p className="mt-2 text-sm text-foreground-600 line-clamp-2">{lead.summary}</p>
              <p className="mt-3 text-xs text-foreground-500">
                <i className="ri-time-line mr-1"></i>
                {lead.publishedAt} · {lead.author}
              </p>
            </div>
          </Link>
        )}
        <div className="flex flex-col gap-3">
          {rest.slice(0, 5).map((item) => (
            <Link
              key={item.id}
              to={`/news/${item.id}`}
              className="group flex items-center gap-4 p-3 md:p-4 rounded-lg bg-background-100 border border-background-200/70 hover:border-accent-500/50 transition-colors"
            >
              <div className="w-24 h-16 md:w-28 md:h-[72px] shrink-0 rounded-md overflow-hidden bg-background-200">
                <img
                  src={item.cover}
                  alt={item.title}
                  title={`${item.title} 影视资讯`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="min-w-0">
                <span className="text-xs text-secondary-300">{CATEGORY_LABEL[item.category]}</span>
                <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-accent-400 transition-colors">
                  {item.title}
                </h3>
                <p className="mt-1 text-xs text-foreground-500">
                  <i className="ri-time-line mr-1"></i>
                  {item.publishedAt}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}